import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { GridLayout, useContainerWidth, noCompactor } from 'react-grid-layout';
import { io } from 'socket.io-client';
import { websocketPort } from '../../server/websocketPort';
import Theme from '../../styles/Theme/Theme';
import useUserWidgets from '../../utils/hooks/useUserWidgets';
import useVideowallMode from '../../utils/hooks/useVideowallMode';
import { useAuthContext } from '../../contexts/AuthContext';
import { getEditableZones } from '../../utils/videowallPermissions';
import { MapsWidget } from './MapsWidget';
import { SatisfactionWidget } from './SatisfactionWidget';
import { WeatherWidget } from './WeatherWidget';
import { OperationStatusWidget } from './OperationStatusWidget';
import { SlaMatchingWidget } from './SlaMatchingWidget';
import { ServiceStatusWidget } from './ServiceStatusWidget';
import CustomWidgetRenderer from './CustomWidgetRenderer';
import UrlWidgetRenderer from './UrlWidgetRenderer';
import AddUrlWidgetModal from './AddUrlWidgetModal';

/* noCompactor with overlap allowed — items stay put and can stack */
const freeCompactor = { ...noCompactor, allowOverlap: true };

/* Module-level variable for external drag tracking (reference pattern) */
let draggedItemId = null;

/* ========== CONFIGURATION ========== */
const GRID_COLS = 12;
const ROW_HEIGHT = 30;
const MAX_ROWS = 24;
const CANVAS_HEIGHT = MAX_ROWS * ROW_HEIGHT; /* 720px */

/* Design canvas — must match ZoneA / ZoneB / ZoneC settings */
const DESIGN_CANVAS_WIDTH = 3840;   /* px — geometric canvas width used for scale() */
const DESIGN_CANVAS_HEIGHT = 2160;  /* px — geometric canvas height */
const DESIGN_ROW_HEIGHT = 90;       /* rowHeight used in zone display pages */
/* The theme vw multipliers were calibrated for a 7680px reference canvas.
   --vw must use this base so fonts/spacings render at the intended size. */
const VW_THEME_BASE = 7680;

const ZONES = [
  { key: 'zoneA', title: 'Zona A', endpoint: 'zoneA' },
  { key: 'zoneB', title: 'Zona B', endpoint: 'zoneB' },
  { key: 'zoneC', title: 'Zona C', endpoint: 'zoneC' },
  { key: 'zoneX', title: 'Zona X', endpoint: 'zoneX' },
  { key: 'zoneZ', title: 'Zona Z', endpoint: 'zoneZ' }
];

/* Default widgets that always appear for every user */
const DEFAULT_WIDGETS = [
  { id: 'map',          name: 'Map Widget',       icon: '🗺️', w: 6, h: 12, minW: 1, minH: 1, class: 'Default' },
  { id: 'satisfaction', name: 'Satisfaction',     icon: '😊', w: 3, h: 8,  minW: 1, minH: 1, class: 'Default' },
  { id: 'weather',      name: 'Weather',          icon: '🌤️', w: 3, h: 8,  minW: 1, minH: 1, class: 'Default' },
  { id: 'operation',    name: 'Operation Status', icon: '⚙️', w: 3, h: 8,  minW: 1, minH: 1, class: 'Default' },
  { id: 'sla',          name: 'SLA Matching',     icon: '📊', w: 3, h: 8,  minW: 1, minH: 1, class: 'Default' },
  { id: 'servicestatus', name: 'Service Status',   icon: '📊', w: 4, h: 8,  minW: 2, minH: 4, class: 'Default' }
];

/* Custom widgets are loaded dynamically from the user-configurations API
   via the useUserWidgets hook. See utils/hooks/useUserWidgets.js */

/* Grid config objects for the new react-grid-layout API */
const EDITOR_GRID_CONFIG = {
  cols: GRID_COLS,
  rowHeight: ROW_HEIGHT,
  maxRows: MAX_ROWS,
  margin: [0, 0],
  containerPadding: [0, 0]
};

const EDITOR_DRAG_CONFIG = {
  enabled: true,
  bounded: true,
  cancel: '.no-drag'
};

const EDITOR_RESIZE_CONFIG = {
  enabled: true,
  handles: ['se']
};

const EDITOR_DROP_CONFIG = {
  enabled: true,
  defaultItem: { w: 3, h: 4 }
};

/* ========== STYLED COMPONENTS ========== */
const Container = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: ${Theme.colors?.white || '#fff'};
  border: 1px solid ${Theme.colors?.['neutral-2'] || '#E6EDF0'};
  border-radius: 12px;
`;

const Btn = styled.button`
  padding: 10px 16px;
  border-radius: 16px;
  border: none;
  background: ${p => p.$danger ? '#DC2626' : (Theme.colors?.['green-deloitte'] || '#86BC25')};
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const ZoneBtn = styled.button`
  padding: 8px 14px;
  border-radius: 16px;
  border: 1px solid ${Theme.colors?.['neutral-3'] || '#D1D8DC'};
  background: ${Theme.colors?.white || '#fff'};
  color: ${Theme.colors?.['coolgray-11'] || '#53565A'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  &:hover {
    background: ${Theme.colors?.['green-deloitte'] || '#86BC25'};
    color: #fff;
    border-color: ${Theme.colors?.['green-deloitte'] || '#86BC25'};
  }
`;

const BtnDivider = styled.div`
  width: 1px;
  height: 28px;
  background: ${Theme.colors?.['neutral-2'] || '#E6EDF0'};
  margin: 0 4px;
`;

const CopyFromWrap = styled.div`
  position: relative;
`;

const CopyFromDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: ${Theme.colors?.white || '#fff'};
  border: 1px solid ${Theme.colors?.['neutral-2'] || '#E6EDF0'};
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  z-index: 200;
  min-width: 140px;
  overflow: hidden;
`;

const CopyFromOption = styled.button`
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: ${Theme.colors?.white || '#fff'};
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: ${Theme.colors?.['coolgray-11'] || '#53565A'};
  cursor: pointer;
  &:hover {
    background: ${Theme.colors?.['neutral-0.5'] || '#F5F8FA'};
    color: ${Theme.colors?.['coolgray-12'] || '#222'};
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const StatusText = styled.span`
  margin-left: auto;
  font-size: 13px;
  color: ${Theme.colors?.['coolgray-10'] || '#63666A'};
  font-weight: 600;
`;

const TabsBar = styled.div`
  display: flex;
  gap: 10px;
  background: ${Theme.colors?.white || '#fff'};
  border: 1px solid ${Theme.colors?.['neutral-2'] || '#E6EDF0'};
  border-radius: 12px;
  padding: 12px;
`;

const Tab = styled.button`
  padding: 10px 14px;
  border-radius: 16px;
  border: 2px solid ${p => p.$active
    ? (Theme.colors?.['green-deloitte'] || '#86BC25')
    : (Theme.colors?.['neutral-2'] || '#E6EDF0')};
  background: ${p => p.$active
    ? (Theme.colors?.['green-1'] || '#E3E48D')
    : (Theme.colors?.white || '#fff')};
  color: ${Theme.colors?.['coolgray-12'] || '#222'};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const EditorRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CanvasPanel = styled.div`
  background: ${Theme.colors?.white || '#fff'};
  border-radius: 12px;
  border: 2px solid ${Theme.colors?.['neutral-2'] || '#E6EDF0'};
  padding: 14px;
`;

const CanvasLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${Theme.colors?.['coolgray-12'] || '#222'};
  margin-bottom: 12px;
`;

const Canvas = styled.div`
  background: #050505;
  border: none;
  position: relative;
  height: ${CANVAS_HEIGHT}px;
  overflow: hidden;

  /* grid item styling */
  .react-grid-item {
    border: 2px solid ${Theme.colors?.['green-deloitte'] || '#86BC25'};
    background: #111;
    overflow: hidden;
    border-radius: 16px;
  }

  .react-grid-item > .react-resizable-handle {
    width: 18px;
    height: 18px;
    background: ${Theme.colors?.['green-deloitte'] || '#86BC25'};
    border-radius: 4px 0 6px 0;
  }

  .react-grid-item > .react-resizable-handle::after {
    border-right: 2px solid #fff;
    border-bottom: 2px solid #fff;
    right: 4px;
    bottom: 4px;
  }

  .react-grid-item.react-grid-placeholder {
    background: rgba(134, 188, 37, 0.25) !important;
    border: 2px dashed #86BC25 !important;
    border-radius: 0 !important;
  }
`;

/* ========== TOOLBOX (matches Buildings project style) ========== */
const ToolboxContainer = styled.div`
  background: ${Theme.color?.white || '#fff'};
  border-radius: 12px;
  border: 1px solid ${Theme.color?.['neutral-2'] || '#E6EDF0'};
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
`;

const ToolboxHeader = styled.div`
  display: flex;
  align-items: stretch;
  min-height: 60px;
`;

const ToolboxFilterWrap = styled.div`
  display: flex;
  align-items: center;
  padding: 0 14px;
  border-right: 1px solid ${Theme.color?.['neutral-2'] || '#E6EDF0'};
  background: ${Theme.color?.['neutral-0.5'] || '#F5F8FA'};
  min-width: 180px;
`;

const ToolboxFilterSelect = styled.select`
  width: 100%;
  height: 36px;
  border: 1px solid ${Theme.color?.['neutral-3'] || '#D9D9D9'};
  border-radius: 16px;
  padding: 0 10px;
  color: ${Theme.color?.['coolgray-11'] || '#374151'};
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  outline: none;
  cursor: pointer;
  &:focus { border-color: ${Theme.color?.['teal-5'] || '#0097A9'}; }
`;

const ToolboxItemsArea = styled.div`
  flex: 1;
  display: flex;
  gap: 10px;
  padding: 10px 14px;
  overflow-x: auto;
  align-items: center;

  /* Thin scrollbar */
  &::-webkit-scrollbar { height: 5px; }
  &::-webkit-scrollbar-track { background: ${Theme.color?.['neutral-1'] || '#EEF2F4'}; border-radius: 3px; }
  &::-webkit-scrollbar-thumb { background: ${Theme.color?.['coolgray-4'] || '#BBBCBC'}; border-radius: 3px; }
`;

const ToolboxCard = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 100px;
  height: 100px;
  background: ${Theme.color?.['neutral-0.5'] || '#F5F8FA'};
  border: 2px solid ${Theme.color?.['neutral-2'] || '#E6EDF0'};
  border-radius: 12px;
  padding: 8px 6px;
  cursor: grab;
  user-select: none;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;

  &:hover {
    border-color: ${Theme.color?.['teal-5'] || '#0097A9'};
    box-shadow: 0 4px 12px rgba(0, 151, 169, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    cursor: grabbing;
    opacity: 0.7;
    transform: translateY(0);
  }
`;

const CardIcon = styled.div`
  font-size: 26px;
  line-height: 1;
`;

const CardName = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${Theme.color?.['coolgray-11'] || '#53565A'};
  line-height: 1.2;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WidgetInner = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  cursor: move;
`;

/* Fills the entire grid cell with the widget preview.
   pointer-events: none ensures drag/resize still works. */
const WidgetPreviewBody = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

/**
 * WidgetScaleWrap
 *
 * Renders its children at the DESIGN canvas proportions and then scales
 * them down so the preview cell looks exactly like a miniature of the
 * real zone display.
 *
 * How it works:
 *  - We expand the container to the "virtual" design-canvas-relative size
 *    (e.g. 1 / 0.365 = 2.74× the editor cell width) and then apply
 *    transform: scale(scaleX, scaleY) with transform-origin top-left.
 *  - The `--vw` custom property mirrors what useVideowallMode() injects
 *    on zone display pages, so all calc(N * var(--vw)) expressions resolve
 *    correctly to their design-size values before being scaled.
 *  - `data-display-mode="videowall"` activates the videowall CSS branches
 *    in Text, WeatherWidget, RadarChart, etc. for the same reason.
 */
const WidgetScaleWrap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  /* expand to design-canvas virtual size, then scale back down */
  width: ${p => 100 / p.$sx}%;
  height: ${p => 100 / p.$sy}%;
  transform: scale(${p => p.$sx}, ${p => p.$sy});
  transform-origin: top left;
  overflow: hidden;
`;

const Grip = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  font-size: 18px;
  color: ${Theme.colors?.['green-deloitte'] || '#86BC25'};
  z-index: 2;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  line-height: 1;
  pointer-events: none;
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  border: none;
  background: #DC2626;
  color: #fff;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 7px;
  cursor: pointer;
  z-index: 2;
`;


const EmptyMsg = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #777;
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: #1a2332;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a7fa5;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.05em;
  border-radius: 4px;
`;

const RetryBtn = styled.button`
  display: inline-block;
  margin-left: 8px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid ${Theme.color?.['teal-5'] || '#0097A9'};
  background: #fff;
  color: ${Theme.color?.['teal-5'] || '#0097A9'};
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: ${Theme.color?.['teal-1'] || '#DDEFE8'}; }
`;

const CardBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  padding: 1px 5px;
  border-radius: 6px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  background: ${p => p.$custom ? (Theme.color?.['teal-3'] || '#6FC2B4') : (Theme.color?.['green-deloitte'] || '#86BC25')};
  color: #fff;
`;

const ToolboxDeleteBtn = styled.button`
  position: absolute;
  top: 4px;
  left: 4px;
  width: 14px;
  height: 14px;
  border-radius: 2px;
  border: none;
  background: #DC2626;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 3;
  opacity: 0.85;
  transition: opacity 0.15s;
  &:hover { opacity: 1; }
`;

const ToolboxEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  color: ${Theme.color?.['coolgray-7'] || '#97999B'};
  font-size: 13px;
  font-weight: 600;
  flex: 1;
`;

const AddWidgetBtnWrap = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-left: 1px solid ${Theme.color?.['neutral-2'] || '#E6EDF0'};
  flex-shrink: 0;
`;

const AddWidgetBtn = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 2px dashed ${Theme.color?.['teal-5'] || '#0097A9'};
  background: transparent;
  color: ${Theme.color?.['teal-5'] || '#0097A9'};
  font-size: 26px;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  &:hover {
    background: ${Theme.color?.['teal-1'] || '#DDEFE8'};
    border-color: ${Theme.color?.['teal-6'] || '#007A8A'};
    color: ${Theme.color?.['teal-6'] || '#007A8A'};
    transform: scale(1.08);
  }
  &:active { transform: scale(0.96); }
`;

const MapControlsContainer = styled.div`
  background: ${Theme.color?.white || '#fff'};
  border-radius: 12px;
  border: 1px solid ${Theme.color?.['neutral-2'] || '#E6EDF0'};
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
  align-self: flex-start;
  width: fit-content;
`;

const MapControlsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid ${Theme.color?.['neutral-2'] || '#E6EDF0'};
  background: ${Theme.color?.['neutral-0.5'] || '#F5F8FA'};
`;

const MapControlsTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${Theme.color?.['coolgray-11'] || '#53565A'};
`;

const MapControlsBody = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  flex-wrap: wrap;
  align-items: center;
`;

const LayerToggleBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: 20px;
  border: 2px solid ${p => p.$active
    ? (Theme.color?.['green-deloitte'] || '#86BC25')
    : (Theme.color?.['neutral-3'] || '#D1D8DC')};
  background: ${p => p.$active
    ? (Theme.color?.['green-1'] || '#E3E48D')
    : (Theme.color?.white || '#fff')};
  color: ${Theme.color?.['coolgray-12'] || '#222'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: ${Theme.color?.['green-deloitte'] || '#86BC25'};
    box-shadow: 0 2px 6px rgba(134,188,37,0.2);
  }
`;

const LayerToggleDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$active ? (Theme.color?.['green-deloitte'] || '#86BC25') : '#ccc'};
  flex-shrink: 0;
`;

const toastSlideIn = keyframes`
  from { transform: translateX(120%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const toastSlideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(120%); opacity: 0; }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 90px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ToastItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 10px;
  background: ${p => p.$type === 'success' ? '#065F46' : p.$type === 'warning' ? '#92400E' : '#1E293B'};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 8px 24px rgba(0,0,0,0.25);
  animation: ${p => p.$leaving ? toastSlideOut : toastSlideIn} 0.3s ease forwards;
  min-width: 260px;
  max-width: 400px;
`;

const ToastIcon = styled.span`
  font-size: 18px;
  flex-shrink: 0;
`;


const useToast = (timeout = 2000) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, leaving: false }]);

    // Start leave animation before removal
    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, leaving: true } : t));
    }, timeout - 300);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, timeout);
  }, [timeout]);

  const ToastStack = useMemo(() => {
    const Stack = () => (
      toasts.length > 0 ? (
        <ToastContainer>
          {toasts.map(t => (
            <ToastItem key={t.id} $type={t.type} $leaving={t.leaving}>
              <ToastIcon>{t.type === 'success' ? '\u2714' : t.type === 'warning' ? '\u26A0' : '\u2139'}</ToastIcon>
              {t.message}
            </ToastItem>
          ))}
        </ToastContainer>
      ) : null
    );
    return Stack;
  }, [toasts]);

  return { addToast, ToastStack };
};

/* localStorage key for user-created URL widgets */
const URL_WIDGETS_KEY = 'unified_videowall_url_widgets';

const loadUrlWidgets = () => {
  try {
    return JSON.parse(localStorage.getItem(URL_WIDGETS_KEY) || '[]');
  } catch {
    return [];
  }
};

/* localStorage key for zone canvas layouts */
const ZONE_DATA_KEY = 'unified_videowall_zone_data';

const loadZoneData = () => {
  try {
    return JSON.parse(localStorage.getItem(ZONE_DATA_KEY) || 'null');
  } catch {
    return null;
  }
};

/* Filter types for the toolbox dropdown */
const TOOLBOX_FILTER = { ALL: 'all', DEFAULT: 'default', CUSTOM: 'custom', URL: 'url' };

/* Map layer toggle definitions */
const MAP_LAYERS = [
  { key: 'showTraffic',         label: 'Tráfego'      },
  { key: 'showIncidents',       label: 'Incidentes'   },
  { key: 'showRealTimeVehicle', label: 'Autocarros'   },
];

/* ========== COMPONENT ========== */
export const VideoWallGrid = ({ onZoneUpdate }) => {
  // Inject --vw on <html> so CustomIcon (which reads document.documentElement)
  // scales SVG icons to match the VW_THEME_BASE calibration, same as zone pages.
  useVideowallMode(VW_THEME_BASE, 2160);
  useEffect(() => { document.title = 'Controller'; }, []);
  const { tags } = useAuthContext();
  const editableZoneKeys = useMemo(() => getEditableZones(tags), [tags]);
  const allowedZones = useMemo(() => ZONES.filter(z => editableZoneKeys.includes(z.key)), [editableZoneKeys]);
  const [activeZone, setActiveZone] = useState(() => editableZoneKeys[0] || 'zoneA');
  const [toolboxFilter, setToolboxFilter] = useState(TOOLBOX_FILTER.ALL);
  const [isDragging, setIsDragging] = useState(false);
  const [urlWidgets, setUrlWidgets] = useState(() => loadUrlWidgets());
  const [showAddModal, setShowAddModal] = useState(false);
  const [mapLayerConfig, setMapLayerConfig] = useState({
    showTraffic: true,
    showIncidents: true,
    showRealTimeVehicle: true,
  });
  const [showCopyFrom, setShowCopyFrom] = useState(false);
  // Server-deployed zone config received via socket (for copy-from-zone)
  const [serverZoneConfig, setServerZoneConfig] = useState(null);
  const socketRef = useRef(null);

  // Connect to socket and listen for the server's saved zone config
  useEffect(() => {
    const s = io(websocketPort, { transports: ['websocket'], reconnection: true });
    socketRef.current = s;
    s.on('videowall_zone_update', (data) => {
      if (data?.zones) setServerZoneConfig(data.zones);
    });
    return () => s.disconnect();
  }, []);

  const [zoneData, setZoneData] = useState(() => {
    const defaults = {
      zoneA: { items: [], layouts: [] },
      zoneB: { items: [], layouts: [] },
      zoneC: { items: [], layouts: [] },
      zoneX: { items: [], layouts: [] },
      zoneZ: { items: [], layouts: [] },
    };
    const saved = loadZoneData();
    if (!saved) return defaults;
    // Merge so that new zones added after the user last saved are present
    return { ...defaults, ...saved };
  });

  const { addToast, ToastStack } = useToast(3500);

  /* Fetch user-saved custom widgets from the user-configurations API */
  const { widgets: customWidgets, loading: widgetsLoading, error: widgetsError, refetch: refetchWidgets } = useUserWidgets();

  /* Merge default widgets + API custom widgets + local URL widgets */
  const widgetCatalog = useMemo(() => {
    const customIds = new Set(customWidgets.map(w => w.id));
    const urlIds = new Set(urlWidgets.map(w => w.id));
    return [
      ...DEFAULT_WIDGETS.filter(w => !customIds.has(w.id) && !urlIds.has(w.id)),
      ...customWidgets,
      ...urlWidgets
    ];
  }, [customWidgets, urlWidgets]);

  /* Delete a URL widget from toolbox and localStorage */
  const deleteUrlWidget = useCallback((widgetId) => {
    setUrlWidgets(prev => {
      const updated = prev.filter(w => w.id !== widgetId);
      localStorage.setItem(URL_WIDGETS_KEY, JSON.stringify(updated));
      return updated;
    });
    /* Also remove from canvas if it was placed */
    setZoneData(prev => {
      const next = {};
      Object.keys(prev).forEach(key => {
        next[key] = {
          items: prev[key].items.filter(id => id !== widgetId),
          layouts: prev[key].layouts.filter(l => l.i !== widgetId)
        };
      });
      return next;
    });
    addToast('Widget removido', 'warning');
  }, [addToast]);

  const toggleMapLayer = useCallback((key) => {
    setMapLayerConfig(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  /* Create a new URL widget from the modal and persist to localStorage */
  const handleAddUrlWidget = useCallback(({ name, url }) => {
    const newWidget = {
      id: `url_${Date.now()}`,
      name,
      icon: '🔗',
      class: 'URL',
      w: 4,
      h: 8,
      minW: 1,
      minH: 1,
      meta: { url, type: 'url' }
    };
    setUrlWidgets(prev => {
      const updated = [...prev, newWidget];
      localStorage.setItem(URL_WIDGETS_KEY, JSON.stringify(updated));
      return updated;
    });
    setShowAddModal(false);
    addToast(`Widget "${name}" criado com sucesso`, 'success');
  }, [addToast]);

  /* Auto-measure canvas width */
  const { width: canvasWidth, containerRef } = useContainerWidth({ initialWidth: 900 });

  /*
   * Preview scale factors: map from the DESIGN canvas dimensions to the
   * editor canvas dimensions so the widget preview looks proportional to
   * what the user will see after deploy.
   *
   * X: editor canvas pixel width  / design canvas width (3840)
   * Y: editor row height (30)     / design row height   (90)  → always 1/3
   */
  const previewScaleX = (canvasWidth || 900) / DESIGN_CANVAS_WIDTH;
  const previewScaleY = ROW_HEIGHT / DESIGN_ROW_HEIGHT;

  const zone = zoneData[activeZone];
  const mapOnCanvas = zone.items.includes('map');
  const available = useMemo(() => {
    const notOnCanvas = widgetCatalog.filter(w => !zone.items.includes(w.id));
    if (toolboxFilter === TOOLBOX_FILTER.DEFAULT) return notOnCanvas.filter(w => w.class === 'Default');
    if (toolboxFilter === TOOLBOX_FILTER.CUSTOM) return notOnCanvas.filter(w => w.class !== 'Default' && w.class !== 'URL');
    if (toolboxFilter === TOOLBOX_FILTER.URL)    return notOnCanvas.filter(w => w.class === 'URL');
    return notOnCanvas;
  }, [widgetCatalog, zone.items, toolboxFilter]);

  /* ---- Find first free position that doesn't overlap existing items ---- */
  const findFreePosition = useCallback((layouts, w, h) => {
    const occupied = (px, py, pw, ph) =>
      layouts.some(l =>
        px < l.x + l.w && px + pw > l.x &&
        py < l.y + l.h && py + ph > l.y
      );

    for (let y = 0; y <= MAX_ROWS - h; y++) {
      for (let x = 0; x <= GRID_COLS - w; x++) {
        if (!occupied(x, y, w, h)) return { x, y };
      }
    }
    return { x: 0, y: 0 }; // fallback
  }, []);

  /* ---- Add widget to grid ---- */
  const addItem = useCallback((widgetId, pos) => {
    const cat = widgetCatalog.find(w => w.id === widgetId);
    if (!cat) return;

    setZoneData(prev => {
      const z = prev[activeZone];
      if (z.items.includes(widgetId)) return prev;

      // If no explicit position, find the first free spot
      const finalPos = pos || findFreePosition(z.layouts, cat.w, cat.h);

      const newLayoutItem = {
        i: widgetId,
        x: finalPos.x,
        y: finalPos.y,
        w: cat.w,
        h: cat.h,
        minW: cat.minW,
        minH: cat.minH
      };

      return {
        ...prev,
        [activeZone]: {
          items: [...z.items, widgetId],
          layouts: [...z.layouts, newLayoutItem]
        }
      };
    });
  }, [activeZone, findFreePosition, widgetCatalog]);

  /* ---- Remove widget ---- */
  const removeItem = useCallback((widgetId) => {
    setZoneData(prev => {
      const z = prev[activeZone];
      return {
        ...prev,
        [activeZone]: {
          items: z.items.filter(id => id !== widgetId),
          layouts: z.layouts.filter(l => l.i !== widgetId)
        }
      };
    });
  }, [activeZone]);

  /* ---- Update a single item after drag/resize stops (no feedback loop) ---- */
  const updateItemPos = useCallback((layout, oldItem, newItem) => {
    setZoneData(prev => {
      const z = prev[activeZone];
      const updated = z.layouts.map(l => {
        if (l.i !== newItem.i) return l;
        if (l.x === newItem.x && l.y === newItem.y &&
            l.w === newItem.w && l.h === newItem.h) return l;
        return { ...l, x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h };
      });
      return { ...prev, [activeZone]: { ...z, layouts: updated } };
    });
  }, [activeZone]);

  /* ---- Handle drop from toolbox (reference pattern) ---- */
  const handleDrop = useCallback((_layout, layoutItem, _event) => {
    if (!draggedItemId) return;
    addItem(draggedItemId, { x: layoutItem.x, y: layoutItem.y });
    draggedItemId = null;
  }, [addItem]);

  /* ---- Reset zone ---- */
  const reset = useCallback(() => {
    const zoneName = ZONES.find(z => z.key === activeZone)?.title || activeZone;
    setZoneData(prev => ({
      ...prev,
      [activeZone]: { items: [], layouts: [] }
    }));
    addToast(`${zoneName} foi limpa com sucesso`, 'warning');
  }, [activeZone, addToast]);

  /* ---- Helper: get deployed item count for a zone from server config ---- */
  const getServerZoneCount = useCallback((zoneKey) => {
    // Try server config first (works across machines), fallback to localStorage
    const serverZone = serverZoneConfig?.[zoneKey];
    if (serverZone?.layout?.length > 0) return serverZone.layout.length;
    const saved = loadZoneData();
    return saved?.[zoneKey]?.items?.length ?? 0;
  }, [serverZoneConfig]);

  /* ---- Copy layout from a zone's last deployed (saved) state into the active zone ----
   * Reads from the server's saved zone config first so it works on any machine. */
  const copyFromZone = useCallback((sourceZoneKey) => {
    const zoneName = ZONES.find(z => z.key === sourceZoneKey)?.title || sourceZoneKey;

    // Try server-saved config first
    const serverZone = serverZoneConfig?.[sourceZoneKey];
    if (serverZone?.layout?.length > 0) {
      const items = serverZone.layout.map(l => l.widgetId || l.i);
      const layouts = serverZone.layout.map(l => ({
        i: l.widgetId || l.i, x: l.x, y: l.y, w: l.w, h: l.h,
        minW: l.minW, minH: l.minH
      }));
      setZoneData(prev => ({ ...prev, [activeZone]: { items, layouts } }));
      addToast(`Layout de ${zoneName} restaurado com sucesso`, 'success');
      setShowCopyFrom(false);
      return;
    }

    // Fallback to localStorage
    const saved = loadZoneData();
    const source = saved?.[sourceZoneKey];
    if (!source || source.items.length === 0) {
      addToast(`${zoneName} não tem widgets guardados para copiar`, 'warning');
      setShowCopyFrom(false);
      return;
    }
    setZoneData(prev => ({
      ...prev,
      [activeZone]: { items: [...source.items], layouts: source.layouts.map(l => ({ ...l })) }
    }));
    addToast(`Layout de ${zoneName} restaurado com sucesso`, 'success');
    setShowCopyFrom(false);
  }, [activeZone, addToast, serverZoneConfig]);

  /* ---- Deploy to videowall ---- */
  const deploy = useCallback(() => {
    if (!onZoneUpdate) return;

    const zones = {};
    ZONES.forEach(z => {
      if (!editableZoneKeys.includes(z.key) && serverZoneConfig?.[z.endpoint]) {
        zones[z.endpoint] = serverZoneConfig[z.endpoint];
      }
    });
    allowedZones.forEach(z => {
      const d = zoneData[z.key];
      zones[z.endpoint] = {
        enabled: d.items.length > 0,
        layout: d.layouts.map(item => {
          const widgetMeta = widgetCatalog.find(w => w.id === item.i);
          return { ...item, widgetId: item.i, widget: widgetMeta || null };
        })
      };
    });
    localStorage.setItem(ZONE_DATA_KEY, JSON.stringify(zoneData));
    onZoneUpdate({ zones, canvasWidth: 3840, mapLayerConfig });
    addToast('Widgets enviados com sucesso', 'success');
  }, [zoneData, onZoneUpdate, widgetCatalog, addToast, mapLayerConfig, allowedZones, editableZoneKeys, serverZoneConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- Open a zone endpoint in a new tab ---- */
  const openZone = useCallback((zoneKey) => {
    const baseUrl = window.location.origin;
    window.open(`${baseUrl}/${zoneKey}`, `_zone_${zoneKey}`);
  }, []);

  const total = allowedZones.reduce((a, z) => a + (zoneData[z.key]?.items?.length || 0), 0);

  /* Render the real widget component for the canvas preview */
  const renderCanvasWidget = (id, meta) => {
    switch (id) {
      case 'map':        return isDragging
        ? <MapPlaceholder>MAP</MapPlaceholder>
        : <MapsWidget mapOverrides={mapLayerConfig} />;
      case 'satisfaction': return <SatisfactionWidget canvas />;
      case 'weather':    return <WeatherWidget canvas />;
      case 'operation':  return <OperationStatusWidget />;
      case 'sla':        return <SlaMatchingWidget />;
      case 'servicestatus':
      case 'totalavailability':
      case 'opsquality':
      case 'semicircle':         return <ServiceStatusWidget />;
      default:
        if (meta?.meta?.type === 'url') {
          return <UrlWidgetRenderer url={meta.meta.url} />;
        }
        // `meta` here IS the catalog entry — same shape as `item.widget` in Zone pages.
        return <CustomWidgetRenderer widget={meta || { name: id, class: 'Custom', meta: {} }} />;
    }
  };

  return (
    <Container>
      <ToastStack />
      {showAddModal && (
        <AddUrlWidgetModal
          onConfirm={handleAddUrlWidget}
          onClose={() => setShowAddModal(false)}
        />
      )}
      <TopBar>
        <Btn $danger onClick={reset}>Reset</Btn>
        <Btn onClick={deploy}>Deploy</Btn>
        <CopyFromWrap>
          <ZoneBtn onClick={() => setShowCopyFrom(v => !v)}>
            Copiar de zona ▾
          </ZoneBtn>
          {showCopyFrom && (
            <CopyFromDropdown>
              {allowedZones.map(z => {
                const savedCount = getServerZoneCount(z.key);
                return (
                  <CopyFromOption
                    key={z.key}
                    onClick={() => copyFromZone(z.key)}
                    disabled={savedCount === 0}
                  >
                    {z.title}{z.key === activeZone ? ' (atual)' : ''}{savedCount === 0 ? ' — sem deploy' : ` (${savedCount})`}
                  </CopyFromOption>
                );
              })}
            </CopyFromDropdown>
          )}
        </CopyFromWrap>
        <BtnDivider />
        {allowedZones.map(z => (
          <ZoneBtn key={z.key} onClick={() => openZone(z.key)}>
            {z.title}
          </ZoneBtn>
        ))}
        <StatusText>Widgets: {total}</StatusText>
      </TopBar>

      <TabsBar>
        {allowedZones.map(z => (
          <Tab
            key={z.key}
            $active={activeZone === z.key}
            onClick={() => setActiveZone(z.key)}
          >
            {z.title} ({zoneData[z.key]?.items?.length || 0})
          </Tab>
        ))}
      </TabsBar>

      <EditorRow>
        <CanvasPanel>
          <CanvasLabel>
            Canvas — {ZONES.find(z => z.key === activeZone)?.title}
          </CanvasLabel>
          <Canvas ref={containerRef}>
            <GridLayout
              className="layout"
              width={canvasWidth || 900}
              layout={zone.layouts}
              gridConfig={EDITOR_GRID_CONFIG}
              dragConfig={EDITOR_DRAG_CONFIG}
              resizeConfig={EDITOR_RESIZE_CONFIG}
              dropConfig={EDITOR_DROP_CONFIG}
              compactor={freeCompactor}
              autoSize={false}
              style={{ height: CANVAS_HEIGHT }}
              onDragStart={() => setIsDragging(true)}
              onDragStop={(...args) => { setIsDragging(false); updateItemPos(...args); }}
              onResizeStart={() => setIsDragging(true)}
              onResizeStop={(...args) => { setIsDragging(false); updateItemPos(...args); }}
              onDrop={handleDrop}
            >
              {zone.items.map(id => {
                const meta = widgetCatalog.find(w => w.id === id);
                return (
                  <div key={id}>
                    <WidgetInner>
                      {/* Preview fills the whole cell — pointer-events: none keeps drag working yes */}
                      <WidgetPreviewBody>
                        {/*
                         * WidgetScaleWrap renders the widget at design-canvas proportions
                         * and scales it back to the editor cell size, giving an accurate
                         * miniature preview of how it will look after deploy.
                         *  --vw  : matches what useVideowallMode(7680,2160) sets on zone pages
                         *  data-display-mode: activates the [data-display-mode="videowall"] &
                         *                     CSS branches in Text, WeatherWidget, etc.
                         */}
                        <WidgetScaleWrap
                          $sx={previewScaleX}
                          $sy={previewScaleY}
                          data-display-mode="videowall"
                          style={{
                            '--vw': `${VW_THEME_BASE / 100}px`,
                            '--vh': `${DESIGN_CANVAS_HEIGHT / 100}px`,
                          }}
                        >
                          {renderCanvasWidget(id, meta)}
                        </WidgetScaleWrap>
                      </WidgetPreviewBody>
                      {/* Controls overlay on top of the preview */}
                      <Grip className="drag-handle">&#9776;</Grip>
                      <RemoveBtn className="no-drag" onClick={() => removeItem(id)}>&#10005;</RemoveBtn>
                    </WidgetInner>
                  </div>
                );
              })}
            </GridLayout>

            {zone.items.length === 0 && (
              <EmptyMsg>Arrasta widgets do menu para o canvas</EmptyMsg>
            )}
          </Canvas>
        </CanvasPanel>

        <ToolboxContainer>
          <ToolboxHeader>
            <ToolboxFilterWrap>
              <ToolboxFilterSelect
                value={toolboxFilter}
                onChange={e => setToolboxFilter(e.target.value)}
              >
                <option value={TOOLBOX_FILTER.ALL}>Todos os Widgets</option>
                <option value={TOOLBOX_FILTER.DEFAULT}>Default Widgets</option>
                <option value={TOOLBOX_FILTER.CUSTOM}>Custom Widgets</option>
                <option value={TOOLBOX_FILTER.URL}>Videowall Widgets</option>
              </ToolboxFilterSelect>
            </ToolboxFilterWrap>

            {available.length === 0 ? (
              /* No widgets to show at all — surface loading/error/empty */
              widgetsLoading ? (
                <ToolboxEmpty>A carregar widgets personalizados...</ToolboxEmpty>
              ) : widgetsError ? (
                <ToolboxEmpty>
                  Sem widgets disponíveis &nbsp;
                  <RetryBtn onClick={refetchWidgets}>Tentar novamente</RetryBtn>
                </ToolboxEmpty>
              ) : (
                <ToolboxEmpty>
                  {widgetCatalog.length === 0
                    ? 'Sem widgets configurados'
                    : 'Todos os widgets no canvas'}
                </ToolboxEmpty>
              )
            ) : (
              /* Always render available widgets (defaults are always ready) */
              <ToolboxItemsArea>
                {available.map(w => (
                  <ToolboxCard
                    key={w.id}
                    draggable
                    unselectable="on"
                    onDragStart={e => {
                      draggedItemId = w.id;
                      e.dataTransfer.setData('text/plain', '');
                    }}
                    onDoubleClick={() => addItem(w.id)}
                    title={`${w.name} (${w.class})`}
                  >
                    {w.class === 'URL' && (
                      <ToolboxDeleteBtn
                        className="no-drag"
                        title="Remover widget"
                        onClick={e => { e.stopPropagation(); deleteUrlWidget(w.id); }}
                      >
                        &#10005;
                      </ToolboxDeleteBtn>
                    )}
                    <CardBadge $custom={w.class !== 'Default'}>
                      {w.class === 'Default' ? 'DEF' : w.class === 'URL' ? 'URL' : 'CUSTOM'}
                    </CardBadge>
                    <CardIcon>{w.icon}</CardIcon>
                    <CardName>{w.name}</CardName>
                  </ToolboxCard>
                ))}
                {/* Inline status for custom widgets API */}
                {widgetsLoading && (
                  <ToolboxCard as="div" style={{ cursor: 'default', opacity: 0.6 }}>
                    <CardIcon>⏳</CardIcon>
                    <CardName>A carregar...</CardName>
                  </ToolboxCard>
                )}
                {widgetsError && !widgetsLoading && (
                  <ToolboxCard as="div" style={{ cursor: 'default', opacity: 0.7 }} title={widgetsError}>
                    <CardIcon>⚠️</CardIcon>
                    <CardName>Erro custom</CardName>
                    <RetryBtn className="no-drag" onClick={refetchWidgets} style={{ fontSize: '9px', padding: '2px 6px', marginTop: '2px' }}>↻</RetryBtn>
                  </ToolboxCard>
                )}
              </ToolboxItemsArea>
            )}

            <AddWidgetBtnWrap>
              <AddWidgetBtn
                onClick={() => setShowAddModal(true)}
                title="Adicionar widget de URL (website, YouTube, etc.)"
              >
                +
              </AddWidgetBtn>
            </AddWidgetBtnWrap>
          </ToolboxHeader>
        </ToolboxContainer>

        {mapOnCanvas && (
          <MapControlsContainer>
            <MapControlsHeader>
              <MapControlsTitle>Camadas do Mapa</MapControlsTitle>
            </MapControlsHeader>
            <MapControlsBody>
              {MAP_LAYERS.map(layer => (
                <LayerToggleBtn
                  key={layer.key}
                  $active={mapLayerConfig[layer.key]}
                  onClick={() => toggleMapLayer(layer.key)}
                  title={mapLayerConfig[layer.key] ? `Ocultar ${layer.label}` : `Mostrar ${layer.label}`}
                >
                  {layer.label}
                  <LayerToggleDot $active={mapLayerConfig[layer.key]} />
                </LayerToggleBtn>
              ))}
            </MapControlsBody>
          </MapControlsContainer>
        )}
      </EditorRow>
    </Container>
  );
};

export default VideoWallGrid;
